import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
(function () {
  // Monotonic token to drop stale async responses. A newer variant change
  // increments this; any in-flight callback whose token no longer matches is
  // ignored so it cannot overwrite the latest variant's UI state.
  let latestVariantRequestId = 0;

  function updatePrice() {
    const r = document.querySelector('.md-pk-radio:checked');
    if (!r) return;

    // RE-QUERY elements every time to avoid stale references
    // (DOM elements can become stale when Klaviyo injects/removes elements)
    const addToCartPriceEls = document.querySelectorAll('.mrk-price-btn');
    const subscriptionPriceEls = document.querySelectorAll('.mrk-main-price');
    const onetimePriceEls = document.querySelectorAll('.mrk-onetime-price');
    const compareAtPriceEls = document.querySelectorAll('.compare-at-price');
    const subscribeCheckbox = document.querySelector('.md-subscribe-input');
    const sellingPlan = document.querySelector('[name="selling_plan"]');

    const base = Number(r.getAttribute('base-price')) || 0;
    const onetimePrice = r.getAttribute('price') || ''; // Pack price without subscription discount

    // Get pack size and base compare-at-price for calculating compare-at-price
    const packSize = Number(r.getAttribute('data-pack-size')) || 1;
    const baseCompareAtPrice = Number(r.getAttribute('data-base-compare-at-price')) || 0;
    const baseVariantPrice = Number(r.getAttribute('data-base-variant-price')) || 0;

    // Calculate subscription price (always apply subscription discount for Subscribe section)
    let subscriptionPrice = onetimePrice;
    if (sellingPlan && sellingPlan.selectedOptions.length) {
      const d = Number(sellingPlan.selectedOptions[0].getAttribute('discount')) || 0;
      const discountEl = document.querySelector('.mrk-subscription-discount');
      if (discountEl) discountEl.innerHTML = d;
      const discounted = (base * (1 - d / 100)) / 100;
      subscriptionPrice = (window.cart_currency || '') + discounted.toFixed(2);
    }

    // Subscribe section always shows subscription-discounted price
    subscriptionPriceEls.forEach(el => el.innerHTML = subscriptionPrice);

    // One-time section always shows pack price without subscription discount
    onetimePriceEls.forEach(el => el.innerHTML = onetimePrice);

    // Add to Cart button shows price based on which option is selected
    const isSubscriptionSelected = subscribeCheckbox && subscribeCheckbox.checked;
    const addToCartPrice = isSubscriptionSelected ? subscriptionPrice : onetimePrice;
    addToCartPriceEls.forEach(el => el.innerHTML = addToCartPrice);

    // Update compare-at-price based on pack size
    if (baseCompareAtPrice > 0) {
      const totalCompareAtPrice = (baseCompareAtPrice * packSize) / 100;
      const formattedCompareAtPrice = (window.cart_currency || '$') + totalCompareAtPrice.toFixed(0);
      compareAtPriceEls.forEach(el => el.innerHTML = formattedCompareAtPrice);
    }

    // Calculate and display crossed-out price (base price × quantity)
    const crossoutPrice = (baseVariantPrice * packSize) / 100;
    const formattedCrossoutPrice = (window.cart_currency || '$') + crossoutPrice.toFixed(2);

    // Subscription crossed-out prices (always show, regardless of selection)
    const subscriptionCrossoutEls = document.querySelectorAll('.md-subscription-single-card .mrk-crossout-price');
    subscriptionCrossoutEls.forEach(el => {
      el.style.display = '';
      el.innerHTML = formattedCrossoutPrice;
    });

    // One-time crossed-out prices (only show when pack size > 1)
    const onetimeCrossoutEls = document.querySelectorAll('.md-onetime-card .mrk-crossout-price');
    onetimeCrossoutEls.forEach(el => {
      el.style.display = packSize > 1 ? '' : 'none';
      el.innerHTML = formattedCrossoutPrice;
    });
  }

  // Update pack radio data attributes when variant changes
  function updatePackRadiosWithVariantPrice(newVariantPrice, newCompareAtPrice) {
    const packRadios = document.querySelectorAll('.md-pk-radio');
    packRadios.forEach(radio => {
      const packSize = Number(radio.getAttribute('data-pack-size')) || 1;
      const multidiscountPercent = Number(radio.getAttribute('data-multidiscount-percent')) || 0;

      // Update base variant price
      radio.setAttribute('data-base-variant-price', newVariantPrice);

      // Update compare-at-price if provided
      if (newCompareAtPrice) {
        radio.setAttribute('data-base-compare-at-price', newCompareAtPrice);
      }

      // Calculate new pack prices
      const totalPrice = newVariantPrice * packSize;
      const priceAfterDiscount = totalPrice * (1 - multidiscountPercent / 100);
      const formattedPrice = (window.cart_currency || '$') + (priceAfterDiscount / 100).toFixed(2);

      radio.setAttribute('price', formattedPrice);
      radio.setAttribute('base-price', priceAfterDiscount);
    });
  }

  // Fetch variant data from Shopify API
  function computeVariantCanAddToCart(variantData) {
    if (!variantData) return false;

    // The /products/handle.js and /variants/id.js payloads expose `available`
    // (authoritative; already accounts for inventory tracking + policy) but NOT
    // inventory_quantity / inventory_policy. Trust `available` when present.
    if (typeof variantData.available === 'boolean') return variantData.available;

    if (variantData.inventory_management === 'shopify') {
      const quantity = variantData.inventory_quantity ?? 0;
      if (quantity <= 0 && variantData.inventory_policy === 'deny') {
        return false;
      }
    }

    return true;
  }

  async function fetchVariantPrice(variantId) {
    if (!variantId) return null;

    try {
      // Get current product handle from URL
      const pathParts = window.location.pathname.split('/');
      const productIndex = pathParts.indexOf('products');
      if (productIndex === -1) return null;
      const productHandle = pathParts[productIndex + 1];

      // Fetch product JSON
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) return null;

      const productData = await response.json();
      const variant = productData.variants.find(v => String(v.id) === String(variantId));

      if (variant) {
        return {
          price: variant.price,
          compare_at_price: variant.compare_at_price,
          available: variant.available,
          inventory_quantity: variant.inventory_quantity,
          inventory_management: variant.inventory_management,
          inventory_policy: variant.inventory_policy
        };
      }
    } catch (e) {
      console.error('Error fetching variant price:', e);
    }

    return null;
  }

  // Update add to cart button state based on variant availability
  function updateButtonForAvailability(isAvailable, variantId) {
    const addToCartBtn = document.querySelector('.add-to-cart-button, [id^="BuyButtons-ProductSubmitButton-"]');
    const addToCartTextEl = addToCartBtn?.querySelector('.add-to-cart-text__content');
    const addToCartPriceEl = addToCartBtn?.querySelector('.mrk-price-btn');
    const addToCartDotEl = addToCartBtn?.querySelector('.mrk-dot-price-btn');
    const addToCartIcon = addToCartBtn?.querySelector('.add-to-cart-icon');
    const buyButtonsBlock = document.querySelector('.buy-buttons-block');
    const addToCartButtonContainer = addToCartBtn?.closest('add-to-cart-component');

    if (!addToCartBtn) return;

    // Klaviyo selectors for back-in-stock notification
    const klaviyoSelectors = [
      '[class*="klaviyo"]',
      '[id*="klaviyo"]',
      '[class*="Klaviyo"]',
      '[id*="Klaviyo"]',
      '[class*="back-in-stock"]',
      '[class*="BackInStock"]',
      '[class*="bis-"]',
      '[class*="notify-me"]',
      '[class*="NotifyMe"]',
      '[data-klaviyo]',
      '[data-bis]'
    ];

    if (isAvailable) {
      // Variant is available - enable button
      addToCartBtn.removeAttribute('disabled');
      addToCartBtn.classList.remove('button--sold-out');
      // Clear any stale inline hide from a previous OOS pass; CSS controls visibility.
      addToCartBtn.style.removeProperty('display');
      if (addToCartTextEl) {
        addToCartTextEl.textContent = 'ADD TO CART';
      }
      // Show price and icon
      if (addToCartPriceEl) addToCartPriceEl.style.display = '';
      if (addToCartDotEl) addToCartDotEl.style.display = '';
      if (addToCartIcon) addToCartIcon.style.display = '';

      // Hide Klaviyo elements when variant is available
      if (buyButtonsBlock) {
        klaviyoSelectors.forEach(selector => {
          buyButtonsBlock.querySelectorAll(selector).forEach(el => {
            const text = (el.textContent || '').toLowerCase();
            if (text.includes('notify') || text.includes('back in stock') || text.includes('waitlist')) {
              el.style.display = 'none';
            }
          });
        });
      }

      // Show accelerated checkout buttons
      const acceleratedCheckout = document.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
      if (acceleratedCheckout) {
        acceleratedCheckout.style.display = '';
      }
    } else {
      // Variant is OUT OF STOCK - the CSS rule on product-form-component hides the
      // button; we only mark it disabled so it can't be submitted.
      addToCartBtn.setAttribute('disabled', 'true');

      // Hide price elements
      if (addToCartPriceEl) addToCartPriceEl.style.display = 'none';
      if (addToCartDotEl) addToCartDotEl.style.display = 'none';
      if (addToCartIcon) addToCartIcon.style.display = 'none';

      // Hide accelerated checkout buttons
      const acceleratedCheckout = document.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
      if (acceleratedCheckout) {
        acceleratedCheckout.style.display = 'none';
      }

      // Trigger Klaviyo to re-inject its Back In Stock button
      // Klaviyo needs to be notified when variant changes to out-of-stock
      if (buyButtonsBlock) {
        // Show any existing Klaviyo buttons
        klaviyoSelectors.forEach(selector => {
          buyButtonsBlock.querySelectorAll(selector).forEach(el => {
            const text = (el.textContent || '').toLowerCase();
            if (text.includes('notify') || text.includes('back in stock') || text.includes('waitlist')) {
              el.style.display = '';
            }
          });
        });

        // Trigger Klaviyo's re-detection
        triggerKlaviyoRescan(variantId);
      }
    }
  }

  // Trigger Klaviyo to rescan the page for out-of-stock variants
  function triggerKlaviyoRescan(variantId) {
    // Method 1: Trigger via _klOnsite API
    if (window._klOnsite) {
      try {
        window._klOnsite.trigger();
      } catch (e) {}
    }

    // Method 2: Dispatch events Klaviyo may listen for
    document.dispatchEvent(new Event('klaviyo:pageload'));
    window.dispatchEvent(new Event('klaviyoRescan'));

    // Method 3: Track a viewed product event to trigger Klaviyo
    if (window.klaviyo) {
      try {
        window.klaviyo.push(['track', 'Viewed Product', {
          'VariantId': variantId
        }]);
      } catch (e) {}
    }

    // Method 4: Use _learnq to wake up Klaviyo
    if (window._learnq) {
      try {
        window._learnq.push(['identify', {}]);
      } catch (e) {}
    }
  }

  /**
   * Get the region-specific variant ID from the product form component
   * This is set by Liquid and represents the correct variant for the current region
   */
  function getRegionVariantId() {
    const productFormComponent = document.querySelector('product-form-component');
    return productFormComponent?.getAttribute('data-region-variant-id');
  }

  /**
   * Check if region has inventory from the data attribute set by Liquid
   */
  function getRegionHasInventory() {
    const productFormComponent = document.querySelector('product-form-component');
    const hasInventory = productFormComponent?.getAttribute('data-region-has-inventory');
    return hasInventory === 'true';
  }

  // Handle variant change - fetch new prices and update the button state.
  // Availability is NOT computed here: the market-aware `data-region-has-inventory`
  // attribute (set by region-variant-resolver.liquid on render and by
  // variant-picker.js on change) is the single source of truth. This function
  // only refreshes pricing and re-applies the authoritative availability.
  async function handleVariantChange(variantId) {
    if (!variantId) return;

    // Prefer the explicit variant ID from the triggering event/input.
    // Falling back to the DOM attribute can use stale state during transitions.
    const regionVariantId = getRegionVariantId();
    const variantToCheck = variantId || regionVariantId;

    // Claim a sequencing token; a newer change invalidates this request.
    const requestId = ++latestVariantRequestId;

    const variantData = await fetchVariantPrice(variantToCheck);

    // Drop stale responses: another variant change happened while awaiting.
    if (requestId !== latestVariantRequestId) return;

    // Update prices if variant has price data
    if (variantData && variantData.price) {
      updatePackRadiosWithVariantPrice(variantData.price, variantData.compare_at_price);
      updatePrice();
    }

    // Availability comes from the authoritative, market-aware data attribute.
    const isAvailable = getRegionHasInventory();
    updateButtonForAvailability(isAvailable, variantToCheck);
  }

  // Listen for variant:update events (bag size changes)
  // Attached to document so it reliably catches the bubbling event regardless of section order
  document.addEventListener('variant:update', (event) => {
    const variant = event.detail?.resource;
    const regionVariantId = event.detail?.data?.regionVariantId || variant?.regionVariantId;

    if (variant && variant.price) {
      updatePackRadiosWithVariantPrice(variant.price, variant.compare_at_price);
      setTimeout(updatePrice, 50);
    }

    if (regionVariantId) {
      handleVariantChange(regionVariantId);
    } else if (variant) {
      const isAvailable = computeVariantCanAddToCart(variant);
      updateButtonForAvailability(isAvailable, variant.id);
    }
  });


  // Watch for changes to the hidden variant input as a more reliable trigger
  const variantInput = document.querySelector('input[name="id"]');
  if (variantInput) {
    // Use MutationObserver to detect value changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const newVariantId = variantInput.value;
          if (newVariantId) {
            // Wait for Liquid attributes to update after morph
            setTimeout(() => handleVariantChange(newVariantId), 150);
          }
        }
      });
    });
    observer.observe(variantInput, { attributes: true });

    // Also listen for direct value changes via input events
    variantInput.addEventListener('change', () => {
      if (variantInput.value) {
        // Wait for Liquid attributes to update after morph
        setTimeout(() => handleVariantChange(variantInput.value), 150);
      }
    });
  }

  // Watch for changes to product-form-component's region attributes
  // This ensures we react to Liquid's region-aware updates
  const productFormComponent = document.querySelector('product-form-component');
  if (productFormComponent) {
    const lastRegionObserverValues = {
      'data-region-has-inventory': productFormComponent.getAttribute('data-region-has-inventory'),
      'data-region-variant-id': productFormComponent.getAttribute('data-region-variant-id'),
    };
    const regionObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const attrName = mutation.attributeName;
        if (attrName !== 'data-region-has-inventory' && attrName !== 'data-region-variant-id') return;

        const newValue = productFormComponent.getAttribute(attrName);
        if (lastRegionObserverValues[attrName] === newValue) return;
        lastRegionObserverValues[attrName] = newValue;

        if (attrName === 'data-region-variant-id') {
          if (newValue) {
            setTimeout(() => handleVariantChange(newValue), 50);
          }
          return;
        }

        const variantId = getRegionVariantId();
        if (variantId) {
          const hasInventory = newValue === 'true';
          setTimeout(() => updateButtonForAvailability(hasInventory, variantId), 50);
        }
      });
    });
    regionObserver.observe(productFormComponent, {
      attributes: true,
      attributeFilter: ['data-region-has-inventory', 'data-region-variant-id']
    });
  }

  // Listen to variant picker changes directly
  const variantPicker = document.querySelector('variant-picker');
  if (variantPicker) {
    variantPicker.addEventListener('change', () => {
      // Wait for section render and Liquid attribute updates
      setTimeout(() => {
        const hasInventory = getRegionHasInventory();
        const variantId = getRegionVariantId();
        if (variantId) {
          updateButtonForAvailability(hasInventory, variantId);
        }
      }, 200);
    });
  }

  // Also listen to the dropdown directly
  const variantDropdown = document.querySelector('.variant-option__select');
  if (variantDropdown) {
    variantDropdown.addEventListener('change', () => {
      // Wait for section render and Liquid attribute updates
      setTimeout(() => {
        const hasInventory = getRegionHasInventory();
        const variantId = getRegionVariantId();
        if (variantId) {
          updateButtonForAvailability(hasInventory, variantId);
        }
      }, 200);
    });
  }

  document.addEventListener('change', e => {
    if (e.target.matches('.md-checkbox-input, [name="selling_plan"], .md-pk-radio')) updatePrice();
  });

  document.addEventListener('DOMContentLoaded', () => {
    updatePrice();

    // Sync prices with the actually selected variant on initial page load.
    // Liquid may have pre-rendered pack radio prices from a different variant
    // than the one shown in the dropdown (e.g. region/URL mismatch).
    const selectedOption = document.querySelector('variant-picker select option[selected]')
      || document.querySelector('variant-picker fieldset input:checked');
    const variantId = selectedOption?.dataset?.variantId;
    if (variantId) {
      handleVariantChange(variantId);

      // Sync the hidden variant ID input so Add to Cart submits the correct variant
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput && variantInput.value !== variantId) {
        variantInput.value = variantId;
      }
    }

    if (!variantId) {
      const hasInventory = getRegionHasInventory();
      const regionId = getRegionVariantId();
      if (regionId) {
        updateButtonForAvailability(hasInventory, regionId);
      }
    }
  });

  // ===== Region-based variant filtering =====
  // Filter dropdown options to only show variants available in the current region
  // This runs client-side as a fallback for when section rendering doesn't apply Liquid filters

  async function filterDropdownByRegion() {
    const dropdown = document.querySelector('.variant-option__select');
    if (!dropdown) return;

    // Get current region from Shopify's localization data
    // The Shopify object contains the current country code
    const currentRegion = window.Shopify?.country || 'US';

    // Get product handle from URL
    const pathParts = window.location.pathname.split('/');
    const productIndex = pathParts.indexOf('products');
    if (productIndex === -1) return;
    const productHandle = pathParts[productIndex + 1];

    try {
      // Fetch product data to get all variants
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) return;

      const productData = await response.json();
      const variants = productData.variants || [];

      // Build a map of option values that have a region-matching variant
      const availableOptions = new Set();

      variants.forEach(variant => {
        // Check if this variant's Region option matches current region
        const hasMatchingRegion =
          variant.option1 === currentRegion ||
          variant.option2 === currentRegion ||
          variant.option3 === currentRegion;

        if (hasMatchingRegion) {
          // Add all non-region options to the available set
          [variant.option1, variant.option2, variant.option3].forEach(opt => {
            if (opt && opt !== currentRegion) {
              availableOptions.add(opt);
            }
          });
        }
      });

      // If we found region-based variants, filter the dropdown
      if (availableOptions.size > 0) {
        let hasSelectedVisible = false;
        let firstVisibleOption = null;

        Array.from(dropdown.options).forEach(option => {
          const optionValue = option.value;
          const isAvailable = availableOptions.has(optionValue);

          if (isAvailable) {
            option.style.display = '';
            option.disabled = false;
            if (!firstVisibleOption) firstVisibleOption = option;
            if (option.selected) hasSelectedVisible = true;
          } else {
            option.style.display = 'none';
            option.disabled = true;
          }
        });

        // If currently selected option is now hidden, select the first visible one
        if (!hasSelectedVisible && firstVisibleOption) {
          firstVisibleOption.selected = true;
          dropdown.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    } catch (e) {
      console.error('Error filtering dropdown by region:', e);
    }
  }

  // Run on page load
  document.addEventListener('DOMContentLoaded', filterDropdownByRegion);

  // Also observe for DOM changes (e.g., when variant picker is morphed after section render)
  const variantPickerObserver = new MutationObserver(() => {
    // Debounce to avoid running too frequently
    clearTimeout(variantPickerObserver.timeout);
    variantPickerObserver.timeout = setTimeout(filterDropdownByRegion, 100);
  });

  // Start observing when DOM is ready
  document.addEventListener('DOMContentLoaded', () => {
    const variantPickerContainer = document.querySelector('variant-picker');
    if (variantPickerContainer) {
      variantPickerObserver.observe(variantPickerContainer, {
        childList: true,
        subtree: true
      });
    }
  });
})();

